import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth0 } from '@auth0/auth0-vue'
import { accountService } from 'src/services/api/account.service'
import {
  enrichTicket,
  enrichApproval,
  normalizeTicketForAccount,
  normalizeApprovalForAccount,
} from 'src/services/helpers/account.helper'
import { log } from 'src/services/app/log.service'
import { CACHE_TIMES, QUERY_KEYS, STALE_TIMES } from 'src/services/consts/queries.const'
import { usePostHog } from 'src/composables/app/ui/usePostHog'
import { useFcmPendingUpdates } from 'src/composables/fcm/shared/useFcmPendingUpdates'
import { themeService } from 'src/services/app/theme.service'
import { CACHE_ACTIONS } from 'src/services/consts/app.const'
import { broadcastAccountData } from 'src/composables/app/system/useBroadcastChannel'
import {
  isPracticeActive,
  PRACTICE_QUERY_SCOPE,
} from 'src/composables/onboarding/practice/practiceMode'
import { getPracticeAccount } from 'src/composables/onboarding/practice/practiceSource'
import { isNotFoundLocation } from 'src/router/const'

// Track identified userId per session (survives cache expiration)
let identifiedUserId = ''

export function useAccountQuery() {
  const auth0 = useAuth0()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { identify } = usePostHog()
  const fcmPending = useFcmPendingUpdates()

  const accountIdTypeQuery = useQuery({
    queryKey: [QUERY_KEYS.AUTH0_LOGIN],
    retry: false,
    staleTime: STALE_TIMES.endless,
    gcTime: CACHE_TIMES.endless,
    placeholderData: () => {
      if (!auth0.isAuthenticated.value || auth0.isLoading.value || isNotFoundLocation(router)) {
        return undefined
      }
      return accountService.getAuth0LoginCache() ?? undefined
    },
    enabled: computed(
      () => auth0.isAuthenticated.value && !auth0.isLoading.value && !isNotFoundLocation(router)
    ),
    queryFn: async () => await accountService.auth0Login(),
  })

  const accountDataQuery = useQuery({
    // While the walkthrough runs, the query reads its own entry, seeded with the example set.
    queryKey: computed(() =>
      isPracticeActive.value
        ? [PRACTICE_QUERY_SCOPE, QUERY_KEYS.ACCOUNT_DATA]
        : [QUERY_KEYS.ACCOUNT_DATA]
    ),
    enabled: computed(
      () => !!accountIdTypeQuery.data.value?.accountId && !isNotFoundLocation(router)
    ),
    staleTime: STALE_TIMES.xl,
    gcTime: CACHE_TIMES.xl,
    queryFn: async ({ queryKey }) => {
      if (queryKey[0] === PRACTICE_QUERY_SCOPE) {
        // Never serve null. A missing example set errors this query, and the walkthrough's stay rule
        // reads accountStore.error, so the controller stops the walkthrough.
        const exampleAccount = getPracticeAccount()
        if (!exampleAccount) throw new Error('The walkthrough example account is missing')
        return exampleAccount
      }

      const accountData = await accountService.loadAccount()

      // Identify user only once per session (handles cache expiration correctly)
      if (identifiedUserId !== accountData.userId) {
        identify(accountData.userId, {
          accountId: accountIdTypeQuery.data.value?.accountId,
          username: auth0.user.value.name,
          email: auth0.user.value.email,
          isAdmin: !!accountIdTypeQuery.data.value?.servers?.aimgmt,
        })
        identifiedUserId = accountData.userId
      }

      // Only reconcile when the server sent a theme — an unguarded apply would reset
      // a locally chosen theme to the default on every load while the endpoint is absent.
      if (accountData.settings?.theme) {
        themeService.applyTheme(accountData.settings.theme)
      }

      broadcastAccountData(accountData)
      fcmPending.clearAll()
      return accountData
    },
  })

  const error = computed(() => accountIdTypeQuery.error.value || accountDataQuery.error.value)
  const isLoading = computed(
    () =>
      accountIdTypeQuery.isLoading.value ||
      accountDataQuery.isLoading.value ||
      !accountIdTypeQuery.data.value?.accountId
  )
  const accountData = computed(() => {
    const rawData = accountDataQuery.data.value
    if (!rawData) return null

    const companies = rawData.companies
    return {
      ...rawData,
      tickets: rawData.tickets?.map((ticket) => enrichTicket(ticket, companies)) || [],
      approvals: rawData.approvals?.map((auth) => enrichApproval(auth, companies)) || [],
    }
  })

  const refetch = async () => {
    await accountDataQuery.refetch()
  }

  // Update account tickets - expects camelCase data from services
  const updateAccountTickets = (action, ticketData) => {
    const accountData = queryClient.getQueryData([QUERY_KEYS.ACCOUNT_DATA])
    if (!accountData) return

    let updatedTickets = [...accountData.tickets]

    switch (action) {
      case CACHE_ACTIONS.ADD: {
        // Normalize ticket to account format (minimal fields only)
        const normalizedTicket = normalizeTicketForAccount(ticketData)
        updatedTickets.push(normalizedTicket)
        break
      }

      case CACHE_ACTIONS.UPDATE: {
        const ticketIdx = updatedTickets.findIndex((t) => t.ticketId === ticketData.ticketId)
        if (ticketIdx !== -1) {
          const normalizedTicket = normalizeTicketForAccount(ticketData)
          updatedTickets[ticketIdx] = {
            ...updatedTickets[ticketIdx],
            ...normalizedTicket,
          }
        }
        break
      }

      case CACHE_ACTIONS.REMOVE: {
        const ticketIdx = updatedTickets.findIndex((t) => t.ticketId === ticketData.ticketId)
        if (ticketIdx !== -1) {
          updatedTickets.splice(ticketIdx, 1)
        }
        break
      }

      default:
        log.error('Invalid action provided to updateAccountTickets:', action)
        return
    }

    queryClient.setQueryData([QUERY_KEYS.ACCOUNT_DATA], {
      ...accountData,
      tickets: updatedTickets,
    })
  }

  // Update account approvals - expects camelCase data from services
  const updateAccountApprovals = (action, approvalData) => {
    const accountData = queryClient.getQueryData([QUERY_KEYS.ACCOUNT_DATA])
    if (!accountData) return

    let updatedApprovals = [...accountData.approvals]

    switch (action) {
      case CACHE_ACTIONS.ADD: {
        // Normalize approval to account format (minimal fields only)
        const normalizedApproval = normalizeApprovalForAccount(approvalData)
        updatedApprovals.push(normalizedApproval)
        break
      }

      case CACHE_ACTIONS.UPDATE: {
        const approvalIdx = updatedApprovals.findIndex(
          (a) => a.approvalId === approvalData.approvalId
        )
        if (approvalIdx !== -1) {
          const normalizedApproval = normalizeApprovalForAccount(approvalData)
          updatedApprovals[approvalIdx] = {
            ...updatedApprovals[approvalIdx],
            ...normalizedApproval,
          }
        }
        break
      }

      case CACHE_ACTIONS.REMOVE: {
        const approvalIdx = updatedApprovals.findIndex(
          (a) => a.approvalId === approvalData.approvalId
        )
        if (approvalIdx !== -1) {
          updatedApprovals.splice(approvalIdx, 1)
        }
        break
      }

      default:
        log.error('Invalid action provided to updateAccountApprovals:', action)
        return
    }

    queryClient.setQueryData([QUERY_KEYS.ACCOUNT_DATA], {
      ...accountData,
      approvals: updatedApprovals,
    })
  }

  return {
    accountData,
    accountIdType: accountIdTypeQuery.data,
    isLoading,
    isRefetching: accountDataQuery.isRefetching,
    error,
    refetch,

    // Actions
    updateAccountTickets,
    updateAccountApprovals,
  }
}
